import { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../../components/ui/button';
import { 
    LayoutDashboard, 
    FileText, 
    Users, 
    Calendar, 
    Settings, 
    LogOut,
    Menu,
    X,
    Sun,
    Moon,
    Plus,
    Palette
} from 'lucide-react';

// Admin Components
import LeadsManagement from './LeadsManagement';
import BlogManagement from './BlogManagement';
import BlogEditor from './BlogEditor';
import BookingsManagement from './BookingsManagement';
import CMSEditor from './CMSEditor';

const sidebarLinks = [
    { icon: LayoutDashboard, name: 'Dashboard', path: '/admin' },
    { icon: Users, name: 'Leads', path: '/admin/leads' },
    { icon: Calendar, name: 'Bookings', path: '/admin/bookings' },
    { icon: FileText, name: 'Blog Posts', path: '/admin/blog' },
    { icon: Palette, name: 'CMS Content', path: '/admin/cms' },
];

function DashboardHome() {
    const [stats, setStats] = useState({
        leads: 0,
        bookings: 0,
        posts: 0,
    });

    return (
        <div data-testid="admin-dashboard-home">
            <h1 className="text-3xl font-bold mb-8">Dashboard</h1>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="p-6 rounded-2xl border border-border bg-background">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                            <Users className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <div className="text-2xl font-bold">{stats.leads}</div>
                            <div className="text-sm text-muted-foreground">Total Leads</div>
                        </div>
                    </div>
                </div>
                
                <div className="p-6 rounded-2xl border border-border bg-background">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                            <Calendar className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                            <div className="text-2xl font-bold">{stats.bookings}</div>
                            <div className="text-sm text-muted-foreground">Booking Requests</div>
                        </div>
                    </div>
                </div>
                
                <div className="p-6 rounded-2xl border border-border bg-background">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                            <FileText className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                            <div className="text-2xl font-bold">{stats.posts}</div>
                            <div className="text-sm text-muted-foreground">Blog Posts</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-6 rounded-2xl border border-border bg-background">
                    <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
                    <div className="space-y-3">
                        <Link 
                            to="/admin/blog/new"
                            className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors"
                        >
                            <Plus className="w-5 h-5 text-primary" />
                            <span>Create New Blog Post</span>
                        </Link>
                        <Link 
                            to="/admin/leads"
                            className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors"
                        >
                            <Users className="w-5 h-5 text-primary" />
                            <span>View Recent Leads</span>
                        </Link>
                        <Link 
                            to="/admin/bookings"
                            className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors"
                        >
                            <Calendar className="w-5 h-5 text-primary" />
                            <span>Manage Bookings</span>
                        </Link>
                    </div>
                </div>

                <div className="p-6 rounded-2xl border border-border bg-background">
                    <h2 className="text-lg font-semibold mb-4">Website Links</h2>
                    <div className="space-y-3">
                        <a 
                            href="/" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors"
                        >
                            <span>View Website</span>
                        </a>
                        <a 
                            href="/blog" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors"
                        >
                            <span>View Blog</span>
                        </a>
                        <a 
                            href="/contact" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/50 transition-colors"
                        >
                            <span>View Contact Page</span>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function AdminDashboard() {
    const { user, loading, logout, isAuthenticated } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (!loading && !isAuthenticated) {
            navigate('/admin/login');
        }
    }, [loading, isAuthenticated, navigate]);

    useEffect(() => {
        setSidebarOpen(false);
    }, [location]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-pulse text-muted-foreground">Loading...</div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return null;
    }

    return (
        <div className="min-h-screen bg-muted/30" data-testid="admin-dashboard">
            {/* Sidebar */}
            <aside 
                className={`fixed inset-y-0 left-0 z-50 w-64 bg-background border-r border-border transform transition-transform lg:translate-x-0 ${
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                <div className="flex flex-col h-full">
                    {/* Logo */}
                    <div className="p-6 border-b border-border">
                        <Link to="/" className="text-xl font-bold text-gradient-primary">
                            Aitrixy Admin
                        </Link>
                    </div>

                    {/* Nav Links */}
                    <nav className="flex-1 p-4 space-y-1">
                        {sidebarLinks.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                                    location.pathname === link.path
                                        ? 'bg-primary text-primary-foreground'
                                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                }`}
                            >
                                <link.icon className="w-5 h-5" />
                                {link.name}
                            </Link>
                        ))}
                    </nav>

                    {/* User Section */}
                    <div className="p-4 border-t border-border">
                        <div className="flex items-center gap-3 mb-4 px-2">
                            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                <span className="text-sm font-medium text-primary">
                                    {user?.name?.[0]?.toUpperCase() || 'A'}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-medium truncate">{user?.name || 'Admin'}</div>
                                <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                            </div>
                        </div>
                        <Button 
                            variant="outline" 
                            className="w-full justify-start"
                            onClick={logout}
                            data-testid="admin-logout-btn"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Sign Out
                        </Button>
                    </div>
                </div>
            </aside>

            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Main Content */}
            <div className="lg:pl-64">
                {/* Top Bar */}
                <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border">
                    <div className="flex items-center justify-between h-16 px-6">
                        <button
                            onClick={() => setSidebarOpen(!sidebarOpen)}
                            className="lg:hidden p-2 rounded-lg hover:bg-muted"
                        >
                            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={toggleTheme}
                                className="p-2 rounded-lg hover:bg-muted transition-colors"
                                aria-label="Toggle theme"
                            >
                                {theme === 'light' ? (
                                    <Moon className="w-5 h-5" />
                                ) : (
                                    <Sun className="w-5 h-5" />
                                )}
                            </button>
                        </div>
                    </div>
                </header>

                {/* Page Content */}
                <main className="p-6">
                    <Routes>
                        <Route index element={<DashboardHome />} />
                        <Route path="leads" element={<LeadsManagement />} />
                        <Route path="bookings" element={<BookingsManagement />} />
                        <Route path="blog" element={<BlogManagement />} />
                        <Route path="blog/new" element={<BlogEditor />} />
                        <Route path="blog/edit/:id" element={<BlogEditor />} />
                        <Route path="cms" element={<CMSEditor />} />
                    </Routes>
                </main>
            </div>
        </div>
    );
}
